import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  MotionContext,
  MOTION_STORAGE_KEY,
} from "@/hooks/useMotionPreference";

function getSystemReducedMotion(): boolean {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
  return false;
}

function getInitialReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem(MOTION_STORAGE_KEY);
  if (stored === "on") return true;
  if (stored === "off") return false;
  return getSystemReducedMotion();
}

export default function MotionPreferenceProvider({ children }: { children: ReactNode }) {
  const [reducedMotion, setReducedState] = useState<boolean>(getInitialReducedMotion);

  useEffect(() => {
    document.documentElement.classList.toggle("reduce-motion", reducedMotion);
  }, [reducedMotion]);

  const setReducedMotion = useCallback((value: boolean) => {
    setReducedState(value);
    try {
      window.localStorage.setItem(MOTION_STORAGE_KEY, value ? "on" : "off");
    } catch {
      // almacenamiento no disponible: se mantiene solo en memoria
    }
  }, []);

  const value = useMemo(
    () => ({ reducedMotion, setReducedMotion }),
    [reducedMotion, setReducedMotion],
  );

  return <MotionContext.Provider value={value}>{children}</MotionContext.Provider>;
}